import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { getPhoneNumberById, deletePhoneNumber } from "@/api/phone-numbers/phone_numbers.api";
import type { PhoneNumberDetails as PhoneNumberDetailsType } from "@/api/phone-numbers/phone_numbers.types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OutboundCallTab } from "./OutboundCallTab";
import { InboundTab } from "./InboundTab";
import { getAssistants } from "@/api/assistants/assistants.api";
import type { Assistant } from "@/api/assistants/assistants.types";
import { getMultiAgents } from '@/api/multi-agent/multi_agent.api';
import type { MultiAgent } from '@/api/multi-agent/multi_agent.types';
import { 
    ArrowLeft, 
    Loader2, 
    Trash2,
    AlertCircle,
    CheckCircle2,
    Ban,
    PhoneIncoming,
    PhoneOutgoing,
    Save,
    PhoneCall
} from "lucide-react";
import { PhoneNumberDetailsSkeleton } from "./phone-number-details-skeleton";
import { useAuth } from '@/context/AuthContext';
import { SiteHeader } from "@/components/site-header";

const PhoneNumberDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { error, success } = useEnhancedToast();
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState<PhoneNumberDetailsType | null>(null);
    const [assistants, setAssistants] = useState<Assistant[]>([]);
    const [multiAgents, setMultiAgents] = useState<MultiAgent[]>([]);
    const [activeTab, setActiveTab] = useState<string>("inbound");
    const [isOutboundFormValid, setIsOutboundFormValid] = useState(false);
    const { user } = useAuth();
    const canDeletePhoneNumber = user?.permissions?.phone_numbers === 'EDITOR';
    
    // Refs to trigger save/submit from child tabs
    const inboundFormRef = useRef<HTMLFormElement>(null);
    const outboundFormRef = useRef<HTMLFormElement>(null);

    // Create refs for toast functions
    const errorRef = useRef(error);
    const successRef = useRef(success);

    // Update refs when toast functions change
    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    useEffect(() => {
        const fetchPhoneNumber = async () => {
            if (!id) return;
            setIsLoading(true);
            try {
                const response = await getPhoneNumberById(id);
                if (response.data) {
                    setPhoneNumber(response.data);
                } else if (response.error) {
                    errorRef.current({
                        icon: AlertCircle,
                        title: "Failed to Load Phone Number",
                        description: response.error,
                        duration: 4000,
                    });
                    navigate('/dashboard/phone-numbers');
                }
            } catch (err) {
                errorRef.current({
                    icon: AlertCircle,
                    title: "Error Loading Phone Number",
                    description: err instanceof Error ? err.message : "Could not fetch phone number details",
                    duration: 4000,
                });
                navigate('/dashboard/phone-numbers');
            } finally {
                setIsLoading(false);
            }
        };

        fetchPhoneNumber();
    }, [id, navigate]); 

    useEffect(() => {
        const fetchAssistants = async () => {
            try {
                const response = await getAssistants();
                if (response.data) {
                    setAssistants(response.data);
                } else if (response.error) {
                    errorRef.current({
                        icon: AlertCircle,
                        title: "Failed to Load Assistants",
                        description: response.error,
                        duration: 4000,
                    });
                }
            } catch (err) {
                errorRef.current({
                    icon: AlertCircle,
                    title: "Error Loading Assistants",
                    description: err instanceof Error ? err.message : "Could not fetch assistants",
                    duration: 4000,
                });
            }
        };

        const fetchMultiAgents = async () => {
            try {
                const response = await getMultiAgents();
                if (!response.error && response.data) {
                    setMultiAgents(response.data);
                } else {
                    errorRef.current({
                        icon: AlertCircle,
                        title: "Failed to Load Multi-Agents",
                        description: response.error,
                        duration: 4000,
                    });
                }
            } catch (err) {
                errorRef.current({
                    icon: AlertCircle,
                    title: "Error Loading Multi-Agents",
                    description: err instanceof Error ? err.message : "Could not fetch multi agents",
                    duration: 4000,
                });
            }
        };

        fetchAssistants();
        fetchMultiAgents();
    }, []); 

    const handleDelete = async () => {
        if (!canDeletePhoneNumber) {
            errorRef.current({
                icon: Ban,
                title: "Permission Denied",
                description: "You need editor permissions to delete phone numbers",
                duration: 4000,
            });
            return;
        }

        setIsDeleting(true);
        try {
            const response = await deletePhoneNumber(id!);
            if (response.error) {
                throw new Error(response.error);
            }
            successRef.current({
                icon: CheckCircle2,
                title: "Phone Number Deleted",
                description: "The phone number has been successfully deleted.",
                duration: 4000,
            });
            navigate("/dashboard/phone-numbers");
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Delete Failed",
                description: err instanceof Error ? err.message : "Could not delete phone number",
                duration: 4000,
            });
        } finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
        }
    };

    if (isLoading) {
        return (
            <PhoneNumberDetailsSkeleton />
        );
    }

    if (!phoneNumber) {
        return null;
    }

    const handleTabAction = () => {
        if (activeTab === "inbound") {
            inboundFormRef.current?.requestSubmit();
        } else {
            outboundFormRef.current?.requestSubmit();
        }
    };

    return (
        <div className="flex flex-col h-full">
            <SiteHeader
                title="Phone Number Details"
                subtitle="Configure inbound and outbound call settings"
                backButton={
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/dashboard/phone-numbers")}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                }
            >
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={isDeleting || !canDeletePhoneNumber}
                    className="gap-2 text-destructive hover:text-destructive"
                >
                    <Trash2 className="h-4 w-4" />
                    Delete
                </Button>
                <Button
                    size="sm"
                    onClick={handleTabAction}
                    className="gap-2"
                    disabled={activeTab === "inbound" ? !canDeletePhoneNumber : (!canDeletePhoneNumber || !isOutboundFormValid)}
                >
                    {activeTab === "inbound" ? (
                        <>
                            <Save className="h-4 w-4" />
                            Save Changes
                        </>
                    ) : (
                        <>
                            <PhoneCall className="h-4 w-4" />
                            Start Outbound Call
                        </>
                    )}
                </Button>
            </SiteHeader>

            <div className="flex-1 overflow-y-auto">
                <div className="px-4 py-6 md:px-8 md:py-8">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                        <div className="flex items-center justify-between">
                            <TabsList className="h-11">
                                <TabsTrigger value="inbound" className="gap-2 px-4">
                                    <PhoneIncoming className="h-4 w-4" />
                                    Inbound Settings
                                </TabsTrigger>
                                <TabsTrigger value="outbound" className="gap-2 px-4">
                                    <PhoneOutgoing className="h-4 w-4" />
                                    Outbound Call
                                </TabsTrigger>
                            </TabsList>
                        </div>

                        <TabsContent value="inbound" className="m-0">
                            {phoneNumber && <InboundTab formRef={inboundFormRef} phoneNumber={phoneNumber} assistants={assistants} multiAgents={multiAgents} />}
                        </TabsContent>

                        <TabsContent value="outbound" className="m-0">
                            {id && (
                                <OutboundCallTab
                                    formRef={outboundFormRef}
                                    assistants={assistants}
                                    phoneNumberId={id}
                                    multiAgents={multiAgents}
                                    onFormValidityChange={setIsOutboundFormValid}
                                />
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader className="space-y-1">
                        <AlertDialogTitle className="text-base">Delete Phone Number?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete this phone number. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel className="h-9">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={isDeleting || !canDeletePhoneNumber}
                            className="h-9 bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-2"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default PhoneNumberDetails;
